// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import {
  Alert,
  AlertTitle,
  Card,
  Checkbox,
  Radio,
  Typography,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { Label } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { classNames } from '@shared/ui-kit/utils';
import {
  NavigationControls,
  ScreenHeader,
  ScreenLayout,
} from '@shared/advisory/account-workflow/layout';
import { useAccountUsage } from './use-account-usage';

export const AccountUsage = defineComponent({
  displayName: 'AccountOpeningAccountUsage',
  useSetup: useAccountUsage,
  View: ({
    t,
    form,
    onNext,
    isFormValid,
    onToggleAccountPurpose,
    accountPurposeOptions,
    monthlyOperationsOptions,
    monthlyTransfersOptions,
    accountChequesOptions,
  }) => {
    return (
      <ScreenLayout
        actions={
          <NavigationControls form="account-usage-form" isFormValid={isFormValid} />
        }
      >
        <div className="w-full">
          <form id="account-usage-form" onSubmit={form.handleSubmit(onNext)}>
            <fieldset className="m-0 flex min-w-0 flex-col gap-7 border-0 p-0">
              <ScreenHeader
                title={
                  <Translation
                    i18nKey="account-opening.screens.account-usage.title"
                    ns="components"
                  />
                }
                subtitle={
                  <Translation
                    i18nKey="account-opening.screens.account-usage.subtitle"
                    ns="components"
                  />
                }
              />

              <Alert
                severity="info"
                className="!rounded-lg !bg-alert-info-bg text-alert-info-text"
                data-testid="account-usage-alert"
              >
                <AlertTitle>
                  <Translation
                    i18nKey="account-opening.account-usage.alert.title"
                    ns="components"
                  />
                </AlertTitle>

                <p>
                  <Translation
                    i18nKey="account-opening.account-usage.alert.description"
                    ns="components"
                  />
                </p>
              </Alert>

              <Card className="!rounded-xl !p-6" variant="outlined">
                <fieldset className="col-span-1 grid gap-2">
                  <Label
                    component="legend"
                    required
                    className="mb-4 !text-h5 !font-semibold !text-title-navy"
                  >
                    <Translation
                      i18nKey="account-opening.account-usage.account-purpose.title"
                      ns="components"
                    />
                  </Label>

                  <Typography
                    variant="body1"
                    className="!mb-4 !text-base !font-normal !text-subtitle"
                  >
                    <Translation
                      i18nKey="account-opening.account-usage.account-purpose.description"
                      ns="components"
                    />
                  </Typography>

                  <Controller
                    control={form.control}
                    name="accountPurpose"
                    render={({ field }) => (
                      <div className="flex flex-col gap-3">
                        {accountPurposeOptions.map(option => {
                          const isSelected = field.value.includes(option.value);

                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => onToggleAccountPurpose(option.value)}
                              className={classNames(
                                'flex w-full items-center gap-3 rounded-2xl border border-border-light bg-white px-4 py-3 text-left transition-all hover:border-primary hover:bg-primary/5',
                                {
                                  'border-primary bg-primary/5 ring-1 ring-primary/20':
                                    isSelected,
                                },
                              )}
                              data-testid={`account-purpose-option-${option.value}`}
                            >
                              <Checkbox
                                checked={isSelected}
                                className="!p-0 !text-primary hover:!bg-transparent"
                                onClick={event => event.stopPropagation()}
                                onChange={() =>
                                  onToggleAccountPurpose(option.value)
                                }
                              />

                              <Typography
                                variant="body1"
                                className="!text-sm !font-normal !text-title-navy sm:!text-base"
                              >
                                {option.label}
                              </Typography>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  />
                </fieldset>
              </Card>

              <Card className="!rounded-xl !p-6" variant="outlined">
                <fieldset className="col-span-1 grid gap-2">
                  <Label
                    component="legend"
                    required
                    className="mb-4 !text-h5 !font-semibold !text-title-navy"
                  >
                    <Translation
                      i18nKey="account-opening.account-usage.monthly-operations.title"
                      ns="components"
                    />
                  </Label>
                  <Controller
                    control={form.control}
                    name="monthlyOperations"
                    render={({ field }) => (
                      <div className="flex flex-col gap-3">
                        {monthlyOperationsOptions.map(option => {
                          const isSelected = field.value === option.value;

                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => field.onChange(option.value)}
                              className={classNames(
                                'flex w-full items-center gap-3 rounded-2xl border border-border-light bg-white px-4 py-3 text-left transition-all hover:border-primary hover:bg-primary/5',
                                {
                                  'border-primary bg-primary/5 ring-1 ring-primary/20':
                                    isSelected,
                                },
                              )}
                              data-testid={`monthly-operations-option-${option.value}`}
                            >
                              <Radio
                                checked={isSelected}
                                className="!p-0 !text-primary hover:!bg-transparent"
                              />

                              <div className="flex flex-wrap items-baseline gap-2">
                                <Typography
                                  variant="body1"
                                  className="!text-sm !font-normal !text-title-navy sm:!text-base"
                                >
                                  {option.label}
                                </Typography>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  />
                </fieldset>
              </Card>

              <Card className="!rounded-xl !p-6" variant="outlined">
                <fieldset className="col-span-1 grid gap-2">
                  <Label
                    component="legend"
                    required
                    className="mb-4 !text-h5 !font-semibold !text-title-navy"
                  >
                    <Translation
                      i18nKey="account-opening.account-usage.monthly-transfers.title"
                      ns="components"
                    />
                  </Label>
                  <Controller
                    control={form.control}
                    name="monthlyTransfers"
                    render={({ field }) => (
                      <div className="flex flex-col gap-3">
                        {monthlyTransfersOptions.map(option => {
                          const isSelected = field.value === option.value;

                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => field.onChange(option.value)}
                              className={classNames(
                                'flex w-full items-center gap-3 rounded-2xl border border-border-light bg-white px-4 py-3 text-left transition-all hover:border-primary hover:bg-primary/5',
                                {
                                  'border-primary bg-primary/5 ring-1 ring-primary/20':
                                    isSelected,
                                },
                              )}
                              data-testid={`monthly-transfers-option-${option.value}`}
                            >
                              <Radio
                                checked={isSelected}
                                className="!p-0 !text-primary hover:!bg-transparent"
                              />

                              <div className="flex flex-wrap items-baseline gap-2">
                                <Typography
                                  variant="body1"
                                  className="!text-sm !font-normal !text-title-navy sm:!text-base"
                                >
                                  {option.label}
                                </Typography>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  />
                </fieldset>
              </Card>

              <Card className="!rounded-xl !p-6" variant="outlined">
                <fieldset className="col-span-1 grid gap-2">
                  <Label
                    component="legend"
                    required
                    className="mb-4 !text-h5 !font-semibold !text-title-navy"
                  >
                    <Translation
                      i18nKey="account-opening.account-usage.account-cheques.question"
                      ns="components"
                    />
                  </Label>
                  <Controller
                    control={form.control}
                    name="accountCheques"
                    render={({ field }) => (
                      <div className="flex flex-col gap-3">
                        {accountChequesOptions.map(option => {
                          const isSelected = field.value === option.value;

                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => field.onChange(option.value)}
                              className={classNames(
                                'flex w-full items-center gap-3 rounded-2xl border border-border-light bg-white px-4 py-3 text-left transition-all hover:border-primary hover:bg-primary/5',
                                {
                                  'border-primary bg-primary/5 ring-1 ring-primary/20':
                                    isSelected,
                                },
                              )}
                              data-testid={`account-cheques-option-${option.value}`}
                            >
                              <Radio
                                checked={isSelected}
                                className="!p-0 !text-primary hover:!bg-transparent"
                              />

                              <div className="flex flex-wrap items-baseline gap-2">
                                <Typography
                                  variant="body1"
                                  className="!text-sm !font-normal !text-title-navy sm:!text-base"
                                >
                                  {option.label}
                                </Typography>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  />
                </fieldset>
              </Card>
            </fieldset>
          </form>
        </div>
      </ScreenLayout>
    );
  },
});
